import React, { useCallback } from 'react';
import {
  Button,
  ChevronRightIcon,
  Fact,
  LedgerHoverCard,
  LedgerRow,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import type { UserItem } from '../organisms/UsersListView';

export interface UserListItemProps {
  user: UserItem;
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  onRowClick?: (id: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  InvitationSent: 'Invited',
  Active: 'Active',
  Inactive: 'Offboarded',
  Offboarding: 'Offboarding',
};

const statusClass = (status?: string): string => {
  if (status === 'Inactive' || status === 'Offboarding') {
    return 'text-content-error';
  }
  if (status === 'InvitationSent') {
    return 'text-content-warning';
  }
  return 'text-content-secondary';
};

const formatJoined = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date);
};

export const UserListItem = React.memo(function UserListItem({
  user,
  columns,
  collapse,
  onRowClick,
}: UserListItemProps) {
  const handleClick = useCallback(() => {
    onRowClick?.(user.id);
  }, [onRowClick, user.id]);

  const role = user.roles[0] ?? 'No role';
  const status = STATUS_LABELS[user.status ?? ''] ?? user.status ?? 'Unknown';
  const joined = formatJoined(user.joinedAt);

  return (
    <LedgerRow
      columns={columns}
      collapse={collapse}
      title={user.fullName}
      cells={{
        role,
        status: <span className={statusClass(user.status)}>{status}</span>,
        joined,
      }}
      meta={`${role} · ${status}`}
      showValues={false}
      hoverCard={
        <LedgerHoverCard
          title={user.fullName}
          kind="User"
          onOpenDetails={handleClick}
          openDetailsLabel="Open profile"
        >
          <Fact label="Email">{user.email}</Fact>
          <Fact label="Role">{role}</Fact>
          <Fact label="Status">{status}</Fact>
          <Fact label="Joined">{joined}</Fact>
        </LedgerHoverCard>
      }
      actions={
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClick}
          aria-label={`Open ${user.fullName}`}
          className="size-8"
        >
          <ChevronRightIcon aria-hidden="true" className="size-4" />
        </Button>
      }
      onClick={handleClick}
      aria-label={`Open user ${user.fullName}`}
      data-testid="user-row"
    />
  );
});
